import React from 'react';
import './TermsOfServicePage.scss';

interface Section {
  number: string;
  title: string;
  content: React.ReactNode;
}

const sections: Section[] = [
  {
    number: '1',
    title: 'Important Legal Notice',
    content: (
      <p>Please read these Terms and Conditions ("Terms") carefully before using the CricCenter platform. By accessing or using our services, you agree to be bound by these Terms. If you do not agree to these Terms, you may not use our platform. These Terms constitute a legally binding agreement between you and CricCenter INC.</p>
    ),
  },
  {
    number: '2',
    title: 'Definitions',
    content: (
      <>
        <p>For the purposes of these Terms:</p>
        <ul>
          <li><strong>"CricCenter," "we," "us," or "our"</strong> refers to CricCenter INC, a company incorporated in the USA.</li>
          <li><strong>"Platform"</strong> refers to the CricCenter mobile application, website (criccenter.com), and all associated services.</li>
          <li><strong>"User," "you," or "your"</strong> refers to any individual or entity that accesses or uses the Platform.</li>
          <li><strong>"Content"</strong> refers to any text, images, video, audio, data, scores, statistics, or other materials on the Platform.</li>
          <li><strong>"Cricket Data"</strong> refers to match scores, statistics, player performance data, team data, and other cricket-related information.</li>
          <li><strong>"Account"</strong> refers to your registered user account on the Platform.</li>
        </ul>
      </>
    ),
  },
  {
    number: '3',
    title: 'Eligibility',
    content: (
      <>
        <p>To use the CricCenter Platform, you must:</p>
        <ul>
          <li>Be at least 13 years of age (or the applicable minimum age in your jurisdiction).</li>
          <li>Have the legal capacity to enter into a binding agreement.</li>
          <li>Not be prohibited from using our services under applicable law.</li>
          <li>Provide accurate and complete registration information.</li>
        </ul>
        <p>If you are under 18, you represent that your parent or legal guardian has reviewed and agreed to these Terms on your behalf.</p>
      </>
    ),
  },
  {
    number: '4',
    title: 'Account Registration',
    content: (
      <p>You must register for an account to access most features of the Platform. You agree to provide accurate, current, and complete information during registration and to keep your account information updated. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. Notify us immediately at criccenter.ai@gmail.com if you suspect unauthorized use of your account.</p>
    ),
  },
  {
    number: '5',
    title: 'Data Collection',
    content: (
      <p>By using our Platform, you acknowledge and agree that CricCenter collects personal information and cricket performance data as described in our Privacy Policy. This includes but is not limited to account details, match data, performance statistics, and usage information. Our data collection practices are governed by our Privacy Policy, which is incorporated into these Terms by reference.</p>
    ),
  },
  {
    number: '6',
    title: 'Data Usage',
    content: (
      <p>CricCenter uses the data collected through the Platform to provide, maintain, and improve our services, personalize your experience, communicate with you, analyze usage patterns, and comply with legal obligations. We may use aggregated, anonymized data for research, analytics, and product development purposes. We do not sell your personal data to third parties for their independent marketing purposes.</p>
    ),
  },
  {
    number: '7',
    title: 'Data Processing',
    content: (
      <p>CricCenter processes user data in accordance with applicable data protection laws, including but not limited to GDPR (for European users), CCPA (for California residents), PIPEDA (for Canadian users), and other applicable regional privacy regulations. By using our Platform, you consent to the processing of your data as described in these Terms and our Privacy Policy.</p>
    ),
  },
  {
    number: '8',
    title: 'Privacy',
    content: (
      <p>Your privacy is important to us. Our Privacy Policy, available on our Platform, describes how we collect, use, share, and protect your personal information. The Privacy Policy is incorporated into these Terms by reference. By using the Platform, you agree to the collection and use of your information as described in the Privacy Policy.</p>
    ),
  },
  {
    number: '9',
    title: 'International Data Transfers',
    content: (
      <p>CricCenter is based in the United States. If you are accessing our Platform from outside the United States, your data may be transferred to and processed in the United States or other countries where we or our service providers operate. By using the Platform, you consent to such international transfers. We take appropriate steps to ensure your data is protected in accordance with applicable laws.</p>
    ),
  },
  {
    number: '10',
    title: 'Consent',
    content: (
      <p>By creating an account and using the Platform, you provide your informed consent to: the collection and processing of your personal data as described in our Privacy Policy; the use of cookies and similar tracking technologies; the receipt of service-related communications; and the processing of your cricket performance data for the purpose of displaying statistics, rankings, and analytics on the Platform.</p>
    ),
  },
  {
    number: '11',
    title: "Children's Policy",
    content: (
      <p>CricCenter does not knowingly collect personal information from children under 13 years of age. If you are a parent or guardian and believe your child has provided us with personal information without your consent, please contact us at criccenter.ai@gmail.com. We will promptly investigate and take appropriate action, including deleting the information if required.</p>
    ),
  },
  {
    number: '12',
    title: 'Content License',
    content: (
      <>
        <p>Subject to these Terms, CricCenter grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Platform for your personal, non-commercial purposes.</p>
        <p>By submitting, uploading, or posting content to the Platform, you grant CricCenter a worldwide, non-exclusive, royalty-free, sublicensable, and transferable license to use, reproduce, distribute, prepare derivative works of, display, and perform such content in connection with the Platform and CricCenter's business.</p>
        <p>You retain ownership of any content you submit to the Platform, but you represent and warrant that you have all rights necessary to grant the above license.</p>
      </>
    ),
  },
  {
    number: '13',
    title: 'Acceptable Use',
    content: (
      <>
        <p>You agree to use the Platform only for lawful purposes and in accordance with these Terms. You agree not to:</p>
        <ul>
          <li>Violate any applicable law or regulation.</li>
          <li>Post or transmit false, misleading, or inaccurate cricket scores or data.</li>
          <li>Harass, abuse, threaten, or harm other users.</li>
          <li>Impersonate any person or entity or misrepresent your affiliation.</li>
          <li>Upload or distribute malware, viruses, or any harmful code.</li>
          <li>Attempt to gain unauthorized access to the Platform or other users' accounts.</li>
          <li>Use the Platform to send unsolicited communications (spam).</li>
          <li>Engage in any activity that disrupts or interferes with the Platform.</li>
        </ul>
      </>
    ),
  },
  {
    number: '14',
    title: 'Third-Party Services',
    content: (
      <p>The Platform may integrate with or contain links to third-party services, applications, or websites. CricCenter is not responsible for the content, privacy practices, or terms of any third-party services. Your use of third-party services is subject to their respective terms and privacy policies. We encourage you to review the terms and privacy policies of any third-party services you use in connection with our Platform.</p>
    ),
  },
  {
    number: '15',
    title: 'Data Ownership and Anti-Scraping',
    content: (
      <>
        <p>CricCenter retains ownership of all Platform-generated data, including aggregated statistics, match data compilations, and derived analytics. The cricket data and scores entered by users remain the property of the respective users or organizations, subject to the license granted in Section 12.</p>
        <p>You agree not to scrape, crawl, spider, or otherwise extract data from the Platform using automated means without our express written permission. Unauthorized data extraction violates these Terms and may violate applicable law. We reserve the right to implement technical measures to prevent unauthorized data extraction and to pursue legal action against violators.</p>
      </>
    ),
  },
  {
    number: '16',
    title: 'Payments and Subscriptions',
    content: (
      <>
        <p>CricCenter offers both free and paid subscription plans. By subscribing to a paid plan, you agree to pay the applicable subscription fees as described on our pricing page.</p>
        <ul>
          <li><strong>Billing:</strong> Subscription fees are billed in advance on a monthly or annual basis, depending on the plan you select.</li>
          <li><strong>Automatic Renewal:</strong> Subscriptions automatically renew at the end of each billing period unless cancelled.</li>
          <li><strong>Cancellation:</strong> You may cancel your subscription at any time through your account settings. Cancellation takes effect at the end of the current billing period.</li>
          <li><strong>Refunds:</strong> Subscription fees are generally non-refundable except where required by applicable law.</li>
          <li><strong>Price Changes:</strong> We reserve the right to change subscription prices. We will notify you in advance of any price changes affecting your subscription.</li>
        </ul>
        <p>Payment processing is handled by secure third-party payment processors. CricCenter does not store your full payment information on its servers.</p>
      </>
    ),
  },
  {
    number: '17',
    title: 'Badges and Achievements',
    content: (
      <p>CricCenter may award badges, achievements, or other digital recognition items to users based on their performance or activity on the Platform. These badges have no monetary value and cannot be transferred, sold, or exchanged for cash. CricCenter reserves the right to modify, revoke, or discontinue any badge or achievement program at any time.</p>
    ),
  },
  {
    number: '18',
    title: 'Prizes and Contests',
    content: (
      <p>From time to time, CricCenter may offer prizes, contests, or promotions. Any such contests or promotions will be subject to separate official rules or terms. CricCenter reserves the right to modify, suspend, or terminate any contest or promotion at any time. Prizes may be subject to applicable taxes, which are the sole responsibility of the winner.</p>
    ),
  },
  {
    number: '19',
    title: 'Live Streaming',
    content: (
      <p>CricCenter may offer live streaming features for matches. By streaming content on the Platform, you agree to comply with all applicable laws and regulations, including those governing broadcast rights and intellectual property. You represent that you have all necessary rights and permissions to stream the content. CricCenter reserves the right to remove or interrupt streams that violate these Terms or applicable law.</p>
    ),
  },
  {
    number: '20',
    title: 'AI Tools and Features',
    content: (
      <>
        <p>CricCenter may provide AI-powered features, including but not limited to performance analysis, match insights, highlight generation, and coaching recommendations. You acknowledge that:</p>
        <ul>
          <li>AI-generated content is for informational purposes only and may not be 100% accurate.</li>
          <li>AI tools should not be relied upon as the sole basis for important decisions.</li>
          <li>CricCenter does not guarantee the accuracy, completeness, or reliability of AI-generated outputs.</li>
          <li>By using AI features, you grant CricCenter permission to use your data to train and improve our AI models, subject to applicable law.</li>
        </ul>
      </>
    ),
  },
  {
    number: '21',
    title: 'Platform Availability',
    content: (
      <p>CricCenter strives to maintain the Platform's availability but does not guarantee uninterrupted access. We may perform scheduled or unscheduled maintenance, updates, or modifications that may temporarily affect availability. CricCenter is not liable for any loss or damage resulting from Platform downtime or unavailability.</p>
    ),
  },
  {
    number: '22',
    title: 'User Responsibilities',
    content: (
      <>
        <p>As a user of the CricCenter Platform, you are responsible for:</p>
        <ul>
          <li>Maintaining the accuracy of data you enter into the Platform, including match scores and player statistics.</li>
          <li>Ensuring you have appropriate authority to represent any team, league, or organization on the Platform.</li>
          <li>Complying with all applicable laws when using the Platform.</li>
          <li>Respecting other users and maintaining a positive community environment.</li>
          <li>Promptly reporting any errors, abuse, or security vulnerabilities you discover.</li>
        </ul>
      </>
    ),
  },
  {
    number: '23',
    title: 'Intellectual Property Rights',
    content: (
      <p>The CricCenter name, logo, brand identity, software, and all Platform content created by CricCenter are protected by copyright, trademark, and other intellectual property laws. You may not copy, reproduce, modify, distribute, or create derivative works of any CricCenter intellectual property without our express written permission. The CricCenter trademark and logo may not be used without prior written approval.</p>
    ),
  },
  {
    number: '24',
    title: 'User Content',
    content: (
      <p>You are solely responsible for all content you submit to the Platform. CricCenter does not endorse any user-generated content. We reserve the right, but are not obligated, to monitor, edit, or remove user content that violates these Terms or that we find objectionable. By submitting content, you represent that the content does not violate any third-party rights, including intellectual property rights and privacy rights.</p>
    ),
  },
  {
    number: '25',
    title: 'Prohibited Content',
    content: (
      <>
        <p>You may not post, upload, or distribute content that:</p>
        <ul>
          <li>Is defamatory, abusive, harassing, threatening, or hateful.</li>
          <li>Infringes any intellectual property rights.</li>
          <li>Contains nudity, pornography, or sexually explicit material.</li>
          <li>Promotes illegal activities or discrimination.</li>
          <li>Contains personally identifiable information of others without their consent.</li>
          <li>Is fraudulent, false, or misleading.</li>
          <li>Violates any applicable law or regulation.</li>
        </ul>
      </>
    ),
  },
  {
    number: '26',
    title: 'Feedback and Suggestions',
    content: (
      <p>Any feedback, suggestions, or ideas you submit to CricCenter regarding the Platform may be used by us without any obligation to compensate you. By submitting feedback, you grant CricCenter a perpetual, irrevocable, worldwide, royalty-free license to use, modify, and incorporate such feedback into our products and services.</p>
    ),
  },
  {
    number: '27',
    title: 'Monitoring and Enforcement',
    content: (
      <p>CricCenter reserves the right to monitor activity on the Platform to ensure compliance with these Terms. We may investigate suspected violations and take appropriate action, including removing content, suspending or terminating accounts, and reporting illegal activity to law enforcement. CricCenter cooperates with law enforcement agencies and complies with court orders and legal process.</p>
    ),
  },
  {
    number: '28',
    title: 'Account Suspension and Termination',
    content: (
      <p>CricCenter reserves the right to suspend or terminate your account at any time, with or without notice, for any violation of these Terms or for any other reason in our sole discretion. Upon termination, your right to use the Platform will immediately cease. Provisions of these Terms that by their nature should survive termination will remain in effect.</p>
    ),
  },
  {
    number: '29',
    title: 'Effect of Termination',
    content: (
      <p>Upon termination of your account, we may delete your account data in accordance with our data retention policies. However, certain data may be retained as required by applicable law or for legitimate business purposes. Cricket match data and statistics may continue to be displayed on the Platform even after account termination, as such data may be associated with public cricket records.</p>
    ),
  },
  {
    number: '30',
    title: 'Modifications to Terms',
    content: (
      <p>CricCenter reserves the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on the Platform and, where appropriate, by notifying you directly. Your continued use of the Platform after the effective date of the modified Terms constitutes your acceptance of the changes. If you do not agree to the modified Terms, you must stop using the Platform.</p>
    ),
  },
  {
    number: '31',
    title: 'Modifications to the Platform',
    content: (
      <p>CricCenter reserves the right to modify, suspend, or discontinue any aspect of the Platform at any time, with or without notice. This includes adding, removing, or changing features and functionality. CricCenter is not liable to you or any third party for any modification, suspension, or discontinuation of the Platform.</p>
    ),
  },
  {
    number: '32',
    title: 'Third-Party Disputes',
    content: (
      <p>CricCenter is not responsible for any disputes between users or between a user and a third party. You agree to resolve any such disputes directly with the other party. You release CricCenter from any claims, demands, and damages arising out of or in any way connected with any such dispute.</p>
    ),
  },
  {
    number: '33',
    title: 'Indemnification',
    content: (
      <p>You agree to indemnify, defend, and hold harmless CricCenter, its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or in any way connected with: your access to or use of the Platform; your violation of these Terms; your violation of any rights of a third party; or your violation of any applicable law.</p>
    ),
  },
  {
    number: '34',
    title: 'Disclaimer of Warranties',
    content: (
      <p>THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. CRICCENTER DOES NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS. YOUR USE OF THE PLATFORM IS AT YOUR SOLE RISK.</p>
    ),
  },
  {
    number: '35',
    title: 'Limitation of Liability',
    content: (
      <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, CRICCENTER AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF OR INABILITY TO USE THE PLATFORM, EVEN IF CRICCENTER HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. CRICCENTER'S TOTAL LIABILITY FOR ANY CLAIMS ARISING UNDER THESE TERMS SHALL NOT EXCEED THE AMOUNT YOU PAID TO CRICCENTER IN THE TWELVE MONTHS PRECEDING THE CLAIM.</p>
    ),
  },
  {
    number: '36',
    title: 'Force Majeure',
    content: (
      <p>CricCenter shall not be liable for any failure or delay in performance resulting from causes beyond our reasonable control, including but not limited to acts of God, natural disasters, pandemics, war, terrorism, civil unrest, government actions, internet outages, power failures, or other unforeseen circumstances.</p>
    ),
  },
  {
    number: '37',
    title: 'Severability',
    content: (
      <p>If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions will continue in full force and effect. The invalid or unenforceable provision will be modified to the minimum extent necessary to make it valid and enforceable.</p>
    ),
  },
  {
    number: '38',
    title: 'Dispute Resolution and Arbitration',
    content: (
      <>
        <p>Any dispute, controversy, or claim arising out of or relating to these Terms or the Platform shall be resolved by binding arbitration in accordance with the rules of the American Arbitration Association (AAA). The arbitration shall be conducted in the English language.</p>
        <p>You agree to waive any right to participate in class action lawsuits or class-wide arbitration against CricCenter. All disputes must be brought in your individual capacity and not as a plaintiff or class member in any purported class action.</p>
        <p>Notwithstanding the above, CricCenter reserves the right to seek injunctive or other equitable relief in any court of competent jurisdiction to protect its intellectual property rights or confidential information.</p>
      </>
    ),
  },
  {
    number: '39',
    title: 'Governing Law',
    content: (
      <p>These Terms shall be governed by and construed in accordance with the laws of the United States and the state of Delaware, without regard to conflict of law principles. For disputes not subject to arbitration, you consent to the exclusive jurisdiction of the federal and state courts located in Delaware, USA.</p>
    ),
  },
  {
    number: '40',
    title: 'Entire Agreement',
    content: (
      <p>These Terms, together with our Privacy Policy and any other agreements incorporated by reference, constitute the entire agreement between you and CricCenter with respect to your use of the Platform and supersede all prior or contemporaneous understandings, agreements, representations, and warranties.</p>
    ),
  },
  {
    number: '41',
    title: 'Waiver',
    content: (
      <p>The failure of CricCenter to enforce any right or provision of these Terms will not be considered a waiver of those rights. Any waiver of any provision of these Terms will be effective only if in writing and signed by a duly authorized representative of CricCenter.</p>
    ),
  },
  {
    number: '42',
    title: 'Assignment',
    content: (
      <p>You may not assign or transfer these Terms, or any of your rights or obligations under these Terms, without CricCenter's prior written consent. CricCenter may assign these Terms or its rights and obligations under these Terms without your consent in connection with a merger, acquisition, or sale of all or substantially all of its assets.</p>
    ),
  },
  {
    number: '43',
    title: 'Notices',
    content: (
      <p>CricCenter may provide notices to you via email to the address associated with your account, or by posting notices on the Platform. You agree that electronic notices are sufficient. You may provide notices to CricCenter by emailing criccenter.ai@gmail.com.</p>
    ),
  },
  {
    number: '44',
    title: 'No Partnership',
    content: (
      <p>Nothing in these Terms shall be deemed to create a partnership, joint venture, agency, or employment relationship between you and CricCenter. You are an independent user of the Platform and do not have authority to bind CricCenter in any way.</p>
    ),
  },
  {
    number: '45',
    title: 'Export Controls',
    content: (
      <p>You agree to comply with all applicable export control laws and regulations. You represent that you are not located in, under the control of, or a national or resident of any country subject to U.S. trade sanctions or embargoes, and that you are not on any U.S. government list of prohibited or restricted parties.</p>
    ),
  },
  {
    number: '46',
    title: 'Compliance with Laws',
    content: (
      <p>You are responsible for complying with all local, state, national, and international laws and regulations that apply to your use of the Platform. CricCenter makes no representation that the Platform is appropriate or available for use in any particular location.</p>
    ),
  },
  {
    number: '47',
    title: 'Accessibility',
    content: (
      <p>CricCenter is committed to making our Platform accessible to users with disabilities. We strive to comply with applicable accessibility standards. If you have difficulty accessing any feature of our Platform, please contact us at criccenter.ai@gmail.com and we will do our best to assist you.</p>
    ),
  },
  {
    number: '48',
    title: 'Beta Features',
    content: (
      <p>CricCenter may offer access to beta or experimental features that are not yet generally available. Such features are provided "as is" without any warranty and may be discontinued at any time. By using beta features, you acknowledge that they may contain bugs or errors and agree to provide feedback to help us improve them.</p>
    ),
  },
  {
    number: '49',
    title: 'API Usage',
    content: (
      <p>If you access the CricCenter API, your use is subject to these Terms and any additional API-specific terms we provide. You may only use the API for approved purposes as specified in your API agreement. Unauthorized use of the API, including excessive requests or data extraction beyond your approved use case, may result in immediate termination of API access and legal action.</p>
    ),
  },
  {
    number: '50',
    title: 'Community Guidelines',
    content: (
      <p>All users are expected to interact with each other respectfully and in the spirit of cricket's traditions of fair play and sportsmanship. Conduct that undermines the positive community environment, including match-fixing, score manipulation, bullying, or promoting hatred, will result in immediate account suspension and may be reported to relevant authorities.</p>
    ),
  },
  {
    number: '51',
    title: 'Reporting Violations',
    content: (
      <p>If you become aware of any content or behavior that violates these Terms, please report it to us at criccenter.ai@gmail.com. Include as much detail as possible, including screenshots or links where relevant. We will investigate all reports and take appropriate action.</p>
    ),
  },
  {
    number: '52',
    title: 'Language',
    content: (
      <p>These Terms are written in English. In the event of any conflict between the English version and any translation, the English version shall prevail. CricCenter may provide translations of these Terms for convenience only.</p>
    ),
  },
  {
    number: '53',
    title: 'Contact Us',
    content: (
      <>
        <p>If you have any questions about these Terms, please contact us:</p>
        <div className="terms-contact-block">
          <p><strong>CricCenter INC</strong></p>
          <p><strong>Company:</strong> CricCenter INC, USA</p>
          <p><strong>Email:</strong> <a href="mailto:criccenter.ai@gmail.com">criccenter.ai@gmail.com</a></p>
        </div>
      </>
    ),
  },
  {
    number: '54',
    title: 'User Acknowledgement',
    content: (
      <p>BY USING THE CRICCENTER PLATFORM, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS AND CONDITIONS. IF YOU DO NOT AGREE TO THESE TERMS, PLEASE DO NOT USE OUR PLATFORM. YOUR CONTINUED USE OF THE PLATFORM FOLLOWING ANY CHANGES TO THESE TERMS CONSTITUTES YOUR ACCEPTANCE OF THOSE CHANGES.</p>
    ),
  },
];

const TermsOfServicePage: React.FC = () => {
  return (
    <div className="terms-page">
      <div className="terms-page-spacer" />

      <section className="terms-hero-section">
        <span className="terms-badge">LEGAL</span>
        <h1 className="terms-hero-title">
          Terms &amp; <em>Conditions</em>
        </h1>
        <p className="terms-hero-subtitle">
          By using CricCenter, you agree to the following terms. Please read them carefully before using our Platform.
        </p>
        <p className="terms-effective-date">Effective Date: June 27, 2026 &nbsp;|&nbsp; Last Updated: June 27, 2026</p>
      </section>

      <div className="terms-content-wrapper">
        <div className="terms-sections">
          {sections.map((section) => (
            <section key={section.number} className="terms-section" id={`section-${section.number}`}>
              <div className="terms-section-header">
                <span className="terms-section-number">{section.number}</span>
                <h2 className="terms-section-title">{section.title}</h2>
              </div>
              <div className="terms-section-body">
                {section.content}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
